import React, { useEffect, useMemo, useState } from 'react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import { formatDate } from '../../utils/dateFormat';

const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return null;
  return date;
};

const startOfWeek = (date) => {
  const day = date.getDay();
  const diff = (day + 6) % 7;
  const start = new Date(date);
  start.setDate(date.getDate() - diff);
  start.setHours(0, 0, 0, 0);
  return start;
};

const formatWeekLabel = (date) => `Week of ${formatDate(date)}`;

const buildWeekGroups = (records) => {
  const groups = new Map();
  records.forEach((record) => {
    const parsed = parseDateValue(record.date);
    if (!parsed) return;
    const weekStart = startOfWeek(parsed);
    const key = weekStart.toISOString().slice(0, 10);
    if (!groups.has(key)) {
      groups.set(key, { key, weekStart, label: formatWeekLabel(weekStart), records: [] });
    }
    groups.get(key).records.push(record);
  });
  return [...groups.values()].sort((a, b) => b.weekStart - a.weekStart);
};

export default function WeeklyGroupedLog({
  records = [],
  filter: controlledFilter,
  onFilterChange,
  storageKey = '',
  isLoading = false,
  error = ''
}) {
  const [internalFilter, setInternalFilter] = useState('all');
  const filter = controlledFilter || internalFilter;
  const setFilter = onFilterChange || setInternalFilter;
  const [expanded, setExpanded] = useState(() => {
    const next = {};
    buildWeekGroups(records).forEach((group) => {
      next[group.key] = { attended: false, absence: true };
    });
    return next;
  });
  const [noteDrafts, setNoteDrafts] = useState({});
  const [notes, setNotes] = useState(() => {
    if (!storageKey || typeof window === 'undefined') return {};
    try {
      return JSON.parse(window.localStorage.getItem(storageKey) || '{}');
    } catch (error) {
      return {};
    }
  });

  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    window.localStorage.setItem(storageKey, JSON.stringify(notes));
  }, [notes, storageKey]);

  const filteredRecords = useMemo(() => {
    if (filter === 'all') return records;
    if (filter === 'attended') return records.filter((r) => r.category === 'attended');
    if (filter === 'absent') return records.filter((r) => r.category === 'absence');
    if (filter === 'unauthorised') return records.filter((r) => r.unauthorised);
    return records;
  }, [records, filter]);

  const weekGroups = useMemo(() => buildWeekGroups(filteredRecords), [filteredRecords]);

  useEffect(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      weekGroups.forEach((group) => {
        if (!next[group.key]) {
          next[group.key] = { attended: false, absence: true };
        }
      });
      return next;
    });
  }, [weekGroups]);

  if (isLoading) {
    return <div className="text-sm sp-muted">Loading attendance logs…</div>;
  }
  if (error) {
    return <div className="sp-alert sp-alert--critical text-sm">{error}</div>;
  }
  if (!records.length) {
    return <div className="text-sm sp-muted">No attendance logs available.</div>;
  }

  const renderRows = (rows, showAddNote) => (
    <div className="space-y-2">
      {rows.map((row) => {
        const noteValue = notes[row.id];
        const draft = noteDrafts[row.id] || '';
        return (
          <div key={row.id} className="sp-panel sp-panel--muted p-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="sp-value" style={{ fontWeight: 600 }}>{row.session}</p>
                <p className="text-xs sp-muted">
                  {formatDate(row.date) || row.date} · {row.statusLabel}
                  {row.reason ? ` · ${row.reason}` : ''}
                  {row.recordedBy ? ` · Recorded by ${row.recordedBy}` : ''}
                </p>
              </div>
              <Badge variant={row.badgeVariant}>{row.badgeLabel}</Badge>
            </div>
            {showAddNote && (
              <div className="mt-3">
                {noteValue ? (
                  <p className="text-xs sp-muted">Note: {noteValue}</p>
                ) : (
                  <>
                    {noteDrafts[row.id] !== undefined ? (
                      <div className="space-y-2">
                        <textarea
                          className="sp-textarea w-full"
                          rows={2}
                          value={draft}
                          onChange={(event) =>
                            setNoteDrafts((prev) => ({ ...prev, [row.id]: event.target.value }))
                          }
                          aria-label={`Add note for ${row.session}`}
                        />
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            type="button"
                            onClick={() => {
                              const trimmed = draft.trim();
                              if (!trimmed) return;
                              setNotes((prev) => ({ ...prev, [row.id]: trimmed }));
                              setNoteDrafts((prev) => {
                                const next = { ...prev };
                                delete next[row.id];
                                return next;
                              });
                            }}
                          >
                            Save note
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            type="button"
                            onClick={() =>
                              setNoteDrafts((prev) => {
                                const next = { ...prev };
                                delete next[row.id];
                                return next;
                              })
                            }
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        type="button"
                        onClick={() => setNoteDrafts((prev) => ({ ...prev, [row.id]: '' }))}
                      >
                        Add note
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Evidence & Logs</h3>
            <p className="text-xs sp-muted">Grouped by week with quick filters.</p>
          </div>
          <div className="sp-segment" role="tablist" aria-label="Attendance filters">
            {[
              { id: 'all', label: 'All' },
              { id: 'attended', label: 'Attended' },
              { id: 'absent', label: 'Absent' },
              { id: 'unauthorised', label: 'Unauthorised' }
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={filter === item.id}
                className={filter === item.id ? 'active' : ''}
                onClick={() => setFilter(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {weekGroups.map((group) => {
        const absences = group.records.filter((record) => record.category === 'absence');
        const attended = group.records.filter((record) => record.category === 'attended');
        const isAbsenceOpen = expanded[group.key]?.absence;
        const isAttendedOpen = expanded[group.key]?.attended;

        return (
          <div key={group.key} className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="sp-value" style={{ fontWeight: 600 }}>{group.label}</h4>
              <span className="text-xs sp-muted">{group.records.length} entries</span>
            </div>

            <div className="sp-panel sp-panel--muted p-3">
              <button
                type="button"
                className="flex items-center justify-between w-full text-left"
                aria-expanded={isAbsenceOpen}
                aria-controls={`absences-${group.key}`}
                onClick={() =>
                  setExpanded((prev) => ({
                    ...prev,
                    [group.key]: { ...prev[group.key], absence: !isAbsenceOpen }
                  }))
                }
              >
                <span className="sp-value" style={{ fontWeight: 600 }}>Absences ({absences.length})</span>
                <Badge variant={absences.length > 0 ? 'danger' : 'inactive'}>
                  {isAbsenceOpen ? 'Hide' : 'Show'}
                </Badge>
              </button>
              {isAbsenceOpen && (
                <div id={`absences-${group.key}`} className="mt-3">
                  {absences.length ? (
                    renderRows(absences, true)
                  ) : (
                    <div className="text-sm sp-muted">No absences recorded for this week.</div>
                  )}
                </div>
              )}
            </div>

            <div className="sp-panel sp-panel--muted p-3">
              <button
                type="button"
                className="flex items-center justify-between w-full text-left"
                aria-expanded={isAttendedOpen}
                aria-controls={`attended-${group.key}`}
                onClick={() =>
                  setExpanded((prev) => ({
                    ...prev,
                    [group.key]: { ...prev[group.key], attended: !isAttendedOpen }
                  }))
                }
              >
                <span className="sp-value" style={{ fontWeight: 600 }}>Attended ({attended.length})</span>
                <Badge variant={attended.length > 0 ? 'review' : 'inactive'}>
                  {isAttendedOpen ? 'Hide' : 'Show'}
                </Badge>
              </button>
              {isAttendedOpen && (
                <div id={`attended-${group.key}`} className="mt-3">
                  {attended.length ? (
                    renderRows(attended, false)
                  ) : (
                    <div className="text-sm sp-muted">No attended sessions logged.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
